import { massageAddressFields } from 'common/utils/addressUtils';
import { cleanMilitaryFields } from 'src/components/MilitaryInformation/utils';
import { mapKeys } from 'src/components/Group/Programs/Edit/utils';
import { uuCompactArrayOrObject, uuOmit, compact, get } from 'lodash';

export function formatEmail(email = {}) {
  return email.email_address && {
    id: email.id,
    acceptable_communication_types: mapKeys(email.acceptable_communication_types),
    email_address: email.email_address,
    is_primary: email.is_primary || false,
  };
}

export function formatPhone(phone = {}) {
  return phone.phone_number && {
    id: phone.id,
    acceptable_communication_types: mapKeys(phone.acceptable_communication_types),
    is_primary: phone.is_primary || false,
    phone_number: phone.phone_number.replace(/\D/g, ''),
    phone_type: phone.phone_type || 'unknown',
  };
}

export function formatContact(values) {
  const emails = get(values, 'email_addresses', []);
  const phones = get(values, 'phone_numbers', []);
  const ssn = get(values, 'ssn', '');
  // Preserve contact_preferences to prevent timeslots from being removed
  const contactPreferences = values.contact_preferences;

  // Convert date_of_birth to number - DateField returns a string timestamp via convertToUnixTimestamp
  // but the API expects an integer
  const dateOfBirth = values.date_of_birth;
  const formattedDateOfBirth = dateOfBirth ? parseInt(dateOfBirth, 10) : undefined;

  const formatted = uuCompactArrayOrObject({
    ...values,
    date_of_birth: formattedDateOfBirth,
    contact_preferences: contactPreferences, // Override with non-compacted version to preserve timeslots
    email_addresses: compact(emails.map((email) => formatEmail(email))),
    military: cleanMilitaryFields(values.military),
    phone_numbers: compact(phones.map((phone) => formatPhone(phone))),
    ssn: ssn.replace(/\D+/ig, ''),
  });

  return formatted;
}

export function formatNewContactDataWithNoInsurance({
  fields = {},
  values = {},
}) {
  const cleanedValues = massageAddressFields(
    values,
    fields.addresses,
  );
  return formatContact(cleanedValues);
}

const ADDRESS_FIELDS_TO_CHECK = [
  'address_type', 'line_1', 'line_2', 'city', 'state', 'postal_code', 'is_mailing_address',
];

function isNonEmptyAddress(addr) {
  if (!addr) return false;
  return ADDRESS_FIELDS_TO_CHECK.some((field) => Boolean(addr[field]));
}

/**
 * Format new contact data from plain values (React Final Form compatible).
 * Unlike formatNewContactDataWithNoInsurance, this does NOT require Redux Form field objects.
 * It filters out empty addresses and adds the default country directly from values.
 *
 * @param {Object} values - Plain form values from React Final Form
 * @returns {Object} Formatted contact data ready for API submission
 */
export function formatNewContactDataFromValues(values) {
  const DEFAULT_COUNTRY = 'United States of America';
  const valuesWithoutInsurance = uuOmit(values, ['insurance', 'insurance_ids']);
  const addressValues = valuesWithoutInsurance.addresses || [];

  const cleanedAddresses = addressValues
    .filter(isNonEmptyAddress)
    .map((addr) => ({ ...addr, country: DEFAULT_COUNTRY }));

  return formatContact({ ...valuesWithoutInsurance, addresses: cleanedAddresses });
}
